import React, { useState, useEffect } from 'react';

import palette from '@lib/core/src/theme/palette';
import { Stack, styled, TextareaAutosize } from '@mui/material';

import Markdown from '@/utils/Markdown';

const PADDING = '4px 8px';
const BORDER_RADIUS = '8px';

const EditableTextArea = styled(TextareaAutosize)({
  all: 'unset',
  padding: PADDING,
  borderRadius: BORDER_RADIUS,
  '&:hover, &:focus': {
    backgroundColor: palette.white,
    border: `1px solid ${palette.grey[1200]}`
  }
});

interface EditableMarkdownProps {
  value: string;
  callback: (text: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

const EditableMarkdown = ({
  value,
  callback,
  placeholder = 'Start writing...',
  style,
  className = 'EditableMarkdown'
}: EditableMarkdownProps) => {
  const [content, setContent] = useState(value);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setContent(value);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setContent(newValue);
    callback(newValue);
  };

  const handleBlur = () => {
    setIsEditing(false);
  };

  return (
    <Stack onClick={() => setIsEditing(true)} className={className}>
      {isEditing ? (
        <EditableTextArea
          minRows={1}
          value={content}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus
          placeholder={placeholder}
          sx={{
            ...style
          }}
        />
      ) : (
        <Stack
          sx={{
            padding: PADDING,
            borderRadius: BORDER_RADIUS,
            border: `1px solid transparent`,
            ...(isEditing
              ? {}
              : {
                  '&:hover': {
                    cursor: 'pointer',
                    backgroundColor: palette.white,
                    border: `1px solid ${palette.grey[1200]}`
                  }
                })
          }}
        >
          <Markdown>{content}</Markdown>
        </Stack>
      )}
    </Stack>
  );
};

export default EditableMarkdown;
